/**
 * Shared hook for document upload and delete operations
 * Used by both Asset Lens and System Lens for consistent document management
 */

import { message } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import { DocumentData } from 'features/baseLens/types/documentTypes'
import { useRolodexEdit } from 'features/fde'
import { DocumentService } from 'features/files'
import { useNavbarData } from 'features/navbar'
import { useSageService } from 'features/sage'
import { useCallback, useMemo } from 'react'

export type EntityType = 'unit' | 'system'

interface UseDocumentOperationsOptions {
  entityType: EntityType
  entityId: string | null | undefined
  siteId?: string
}

interface DocumentOperationResult {
  success: boolean
  document?: DocumentData
  message?: string
}

export const useDocumentOperations = ({
  entityType,
  entityId,
  siteId,
}: UseDocumentOperationsOptions) => {
  // Initialize services
  const { SAGE_URL } = useRuntimeVariables()
  const { rolodexPut, rolodexUploadFile, rolodexDelete } = useRolodexEdit()
  const { organization } = useNavbarData(siteId)
  const { uploadFileToSage } = useSageService()

  // Initialize DocumentService
  const documentService = useMemo(
    () => new DocumentService(SAGE_URL as string),
    [SAGE_URL]
  )

  /**
   * Upload a document with entity-specific tagging
   */
  const uploadDocument = useCallback(
    async (
      file: File,
      metadata: { title: string; description: string },
      onStatusUpdate?: (status: string) => void,
      refreshDocuments?: () => void
    ): Promise<DocumentOperationResult> => {
      // Validate entity selection
      if (!entityId) {
        const entityLabel = entityType === 'unit' ? 'unit' : 'system'
        message.error(`No ${entityLabel} selected`)
        return { success: false, message: `No ${entityLabel} selected` }
      }

      // Validate organization
      if (!organization?.id) {
        message.error('No organization available')
        return { success: false, message: 'No organization available' }
      }

      try {
        onStatusUpdate?.('Validating form...')

        // Determine tags based on entity type
        const tags: Record<string, string> =
          entityType === 'unit' ? { unit: entityId } : { system: entityId }
        tags.organization = organization.id
        tags.site = siteId || ''
        // Use DocumentService to handle the upload
        const result = await documentService.uploadDocument({
          file,
          tags,
          organizationId: organization.id,
          metadata: {
            title: metadata.title || file.name,
            description: metadata.description || '',
          },
          rolodexPut,
          rolodexUploadFile,
          refreshDocuments: refreshDocuments || (() => {}),
          uploadFileToSage,
          onStatusUpdate,
        })

        if (result.success && refreshDocuments) {
          refreshDocuments()
        }

        return result
      } catch (error) {
        console.error('Upload failed:', error)
        return { success: false, message: 'Failed to upload document' }
      }
    },
    [
      entityType,
      entityId,
      organization?.id,
      documentService,
      rolodexPut,
      rolodexUploadFile,
      uploadFileToSage,
      siteId,
    ]
  )

  /**
   * Delete a document
   */
  const deleteDocument = useCallback(
    async (
      documentId: string,
      refreshDocuments?: () => void
    ): Promise<{ success: boolean; message?: string }> => {
      try {
        const result = await documentService.deleteDocument(
          documentId,
          rolodexDelete
        )

        if (result.success && refreshDocuments) {
          refreshDocuments()
        }

        return result
      } catch (error) {
        console.error('Delete failed:', error)
        return { success: false, message: 'Failed to delete document' }
      }
    },
    [documentService, rolodexDelete]
  )

  return {
    uploadDocument,
    deleteDocument,
    documentService,
  }
}

export default useDocumentOperations
